// Form 9 - Alloted Assessors For Assessment
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import ArticleIcon from "@mui/icons-material/Article";
import GavelIcon from "@mui/icons-material/Gavel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventIcon from "@mui/icons-material/Event";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

import { decryptAppId, decryptText } from "../../utils/encryption";
import PageTitle from "../../components/PageTitle";
import axios from "axios";

const NCApplicationChecklist = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [rows, setRows] = useState([]); // ✅ always array
  const [loading, setLoading] = useState(true);

  // 🔐 Encrypted params
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAssessorId = searchParams.get("assessorId");
  const encryptedAssessmentId = searchParams.get("assessmentId");

  // 🔓 Decrypt
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const assessorId = encryptedAssessorId
    ? decryptText(encryptedAssessorId)
    : null;
  const assessmentId = encryptedAssessmentId
    ? decryptText(encryptedAssessmentId)
    : null;

  // 🔄 Fetch NC checklist
  const ncgetapi = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/da-nc/ncchecklist`,
        {
          params: {
            appId,
            orgId,
            assessorId,
            assessmentId,
          },
        }
      );

      console.log("NC API Response:", response.data);

      // ✅ FIX: always set array
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching NC checklist:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appId && orgId && assessorId && assessmentId) {
      ncgetapi();
    } else {
      setLoading(false);
    }
  }, [appId, orgId, assessorId, assessmentId]);

  return (
    <>
      <PageTitle title="NABL | Application Finding" />

      <Box>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <AssignmentTurnedInIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
           Application Finding
          </Typography>
        </Box>

        <Paper elevation={3}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={200}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                No records found
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="5%">#</TableCell>

                    <TableCell width="30%">
                      <Box display="flex" alignItems="center" gap={1}>
                        <ArticleIcon fontSize="small" />
                        Section Name
                      </Box>
                    </TableCell>

                    <TableCell width="15%">
                      <Box display="flex" alignItems="center" gap={1}>
                        <GavelIcon fontSize="small" />
                        Clause No
                      </Box>
                    </TableCell>

                    <TableCell width="30%">
                      <Box display="flex" alignItems="center" gap={1}>
                        <VisibilityIcon fontSize="small" />
                        Observation
                      </Box>
                    </TableCell>

                    <TableCell width="20%">
                      <Box display="flex" alignItems="center" gap={1}>
                        <EventIcon fontSize="small" />
                        Inserted Date
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow hover key={row.id || index}>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell>
                        <Typography fontWeight={500}>
                          {row.SectionName}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {row.clauseNo ? (
                          <Chip
                            label={row.clauseNo}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="N/A" size="small" variant="outlined" />
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip title={row.Observation || ""}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                            }}
                          >
                            {row.Observation || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {row.Insertdate
                            ? new Date(row.Insertdate).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric"
                            })
                            : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default NCApplicationChecklist;
